import { ReactNode, useState, useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  LoadingSpinner,
  H5,
  P2,
  Input,
  Icon,
} from "@focal/ui-kit";

export interface SyncConfirmationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Callback when user confirms the action */
  onConfirm: () => void;
  /** Callback when user declines the action */
  onDecline: () => void;
  /** Whether an operation is in progress */
  isLoading?: boolean;

  // Content configuration
  /** Modal title */
  title: string;
  /** Modal description content (can include ReactNode for formatting) */
  description: ReactNode;
  /** Label for the confirm button */
  confirmLabel: string;
  /** Label for the decline button */
  declineLabel: string;
  /** Whether the confirm button should use destructive (red) styling */
  destructive?: boolean;
  /** If set, user must type this text to enable the confirm button */
  requireTextConfirmation?: string;
  /** Optional data-cy attribute for testing */
  testId?: string;
}

/**
 * A reusable confirmation modal for sync operations.
 * Used for both speaker-to-datatable and datatable-to-speaker sync flows.
 */
export const SyncConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  onDecline,
  isLoading = false,
  title,
  description,
  confirmLabel,
  declineLabel,
  destructive = false,
  requireTextConfirmation,
  testId,
}: SyncConfirmationModalProps) => {
  const [confirmText, setConfirmText] = useState("");

  // Reset text input when modal opens/closes
  useEffect(() => {
    if (!open) setConfirmText("");
  }, [open]);

  const normalize = (s: string) =>
    s.trim().replace(/\s+/g, " ").toLowerCase();

  const isConfirmDisabled =
    isLoading ||
    (requireTextConfirmation
      ? normalize(confirmText) !== normalize(requireTextConfirmation)
      : false);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="max-w-[calc(100vw-32px)] sm:max-w-md rounded-xl"
        data-cy={testId}
      >
        <DialogHeader className="p-2 space-y-4">
          <DialogTitle asChild>
            <H5
              as="h2"
              spacing="none"
              className={
                destructive ? "flex items-center gap-1.5" : undefined
              }
            >
              {destructive && (
                <Icon
                  name="WarningIcon"
                  className="text-destructive shrink-0 !h-[0.95em] !w-[0.95em]"
                  aria-hidden
                />
              )}
              {title}
            </H5>
          </DialogTitle>
          <DialogDescription asChild>
            <P2 textColor="secondary">{description}</P2>
          </DialogDescription>
        </DialogHeader>
        {requireTextConfirmation && (
          <div className="px-2 space-y-2">
            <P2 textColor="secondary">
              Type <strong>{requireTextConfirmation}</strong> to confirm:
            </P2>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={requireTextConfirmation}
              aria-label={`Type ${requireTextConfirmation} to confirm`}
              data-cy="confirmation-text-input"
              autoFocus
            />
          </div>
        )}
        <DialogFooter className="sm:justify-end gap-2 mt-2">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDecline}
              disabled={isLoading}
            >
              {declineLabel}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant={destructive ? "destructive" : "default"}
            size="sm"
            onClick={onConfirm}
            disabled={isConfirmDisabled}
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Updating...
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
